import * as React from 'react'
import {inject, observer} from 'mobx-react'
import {Table, Spin} from 'antd'
import {ColumnProps} from 'antd/lib/table'
import {compose} from 'recompose'
import {createStyles} from '@eda.yandex/ui-kit/lib/jss'
import injectSheet, {WithClasses} from '@eda.yandex/ui-kit/lib/hocs/inject-sheet'
import {StoreType} from 'admin/models/Store'
import {Report} from 'admin/models/ctt/ShiftsTotalReportPage'

type OuterProps = {}
export type InnerProps = OuterProps & WithClasses<typeof styles> & {
  store: StoreType
}

type Zones = Report['attributes']
type ZonesTotal = Zones['intervalTotals']
type Zone = Zones['zoneDetails'][0]['attributes']['zoneData']

type FormattedZone = {
  title: string
  key: string
}

const TABLE_MARGIN = 20
const TABLE_TITLE_COLUMN_WIDTH = 125
const TABLE_CELL_WIDTH = 80
const TABLE_FIXED_HEADER_PADDING = 21
const TABLE_MIN_HEIGHT = 300

// From "2019-03-12T00:00:00+03:00" get "00:00" as HH:mm
const formatReportInterval = (params: {from: string, to: string}) => {
  const from = params.from.slice(11, 16)
  const to = params.to.slice(11, 16)

  return {
    from,
    to,
    key: from + to
  }
}

// format initial zone data to something antd can manage
const formatZoneData = (zoneTitle: string, zoneKey: string | number, zoneData: Zone | ZonesTotal): FormattedZone[] => {
  const zonesTitle = {
    title: zoneTitle,
    key: `${zoneKey}_title`
  }

  const formattedZones: FormattedZone[] = (zoneData as Zone & ZonesTotal).reduce<any>((acc, t) => {
    const {key} = formatReportInterval(t.attributes.timeInterval.attributes)
    const {actual, assigned, planned, freeCouriersCount, activeCouriersCount} = t.attributes as Zone[0]['attributes']

    acc[0] = acc[0] || {title: 'Фактически на смене (чел/час)', key: `${zoneKey}_actual`}
    acc[1] = acc[1] || {title: 'Выбрано по графику (чел/час)', key: `${zoneKey}_assigned`}
    acc[2] = acc[2] || {title: 'План (чел/час)', key: `${zoneKey}_planned`}
    acc[3] = acc[3] || {title: 'Факт от графика (%)', key: `${zoneKey}_enforced`}
    acc[4] = acc[4] || {title: 'Загрузка зоны (%)', key: `${zoneKey}_occupancy`}

    acc[0][key] = actual.toFixed(2)
    acc[1][key] = assigned.toFixed(2)
    acc[2][key] = planned.toFixed(2)

    const enforced = (actual / assigned * 100).toFixed(2)
    if (enforced === 'Infinity') {
      acc[3][key] = '-'
    } else {
      acc[3][key] = +enforced ? enforced : '-'
    }

    if ('activeCouriersCount' in t.attributes && 'freeCouriersCount' in t.attributes) {
      const workload = (activeCouriersCount! / (activeCouriersCount! + freeCouriersCount!) * 100).toFixed(2)
      acc[4][key] = +workload ? workload : '-'
    } else {
      acc[4][key] = '-'
    }

    return acc
  }, [])

  const divider = {
    title: '',
    key: `${zoneKey}_divider`
  }

  return [zonesTitle, ...formattedZones, divider]
}

class ShiftsTotalReport extends React.Component<InnerProps> {
  rootRef?: HTMLDivElement | null

  state = {
    tableComputedHeight: null
  }

  componentDidMount () {
    setTimeout(() => {
      this.setState({
        tableComputedHeight: this.rootRef!.getBoundingClientRect().height
          - TABLE_MARGIN * 2
          - TABLE_FIXED_HEADER_PADDING
      })
    }, 100)
  }

  render () {
    const {classes: c} = this.props
    const {report} = this.props.store.ctt.shiftsTotalReportPage


    if (!report) {
      return null
    }

    const columns: Array<ColumnProps<any>> = [
      {
        dataIndex: 'title',
        key: 'title',
        title: 'Локация',
        fixed: 'left',
        width: TABLE_TITLE_COLUMN_WIDTH
      },
      ...report.attributes.intervalTotals.map((t, i, array) => {
        const {from, to, key} = formatReportInterval(t.attributes.timeInterval.attributes)

        return {
          dataIndex: key,
          title: `${from} ${to}`,
          width: i !== (array.length - 1) ? TABLE_CELL_WIDTH : undefined
        }
      })
    ]

    const dataSource: FormattedZone[] = []

    report.attributes.zoneDetails.forEach((zone) => {
      const {zoneName, zoneId, zoneData} = zone.attributes

      dataSource.push(...formatZoneData(zoneName, zoneId, zoneData))
    })

    if (report.attributes.zoneDetails.length > 1) {
      dataSource.unshift(...formatZoneData('Итого', 'total', report.attributes.intervalTotals))
    }

    return (
      <div className={c.root} ref={(root) => this.rootRef = root}>
        {this.state.tableComputedHeight ?
          <Table
            bordered
            size={'small'}
            columns={columns}
            dataSource={dataSource}
            scroll={
              {
                x: (columns.length) * TABLE_CELL_WIDTH + TABLE_CELL_WIDTH,
                y: this.state.tableComputedHeight! > TABLE_MIN_HEIGHT
                  ? this.state.tableComputedHeight!
                  : TABLE_MIN_HEIGHT
              }}
            rowClassName={(record) => {
              if (record.key.indexOf('title') > -1) {
                return c.titleRow
              }

              return c.row
            }}
            pagination={false}
          /> : <div className={c.loading}><Spin /></div>
        }
      </div>
    )
  }
}

const styles = createStyles({
  '@global': {
    table: {
      tableLayout: 'fixed'
    },
    'td, th': {
      textAlign: 'center'
    },
    '.ant-table-fixed-left table': {
      width: `${TABLE_TITLE_COLUMN_WIDTH}px !important`
    }
  },
  root: {
    margin: TABLE_MARGIN,
    flex: '1 0 auto'
  },
  loading: {
    height: 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  row: {
    background: 'white'
  },
  titleRow: {
    composes: '$row',
    background: 'aliceblue'
  }
})

export default compose<InnerProps, OuterProps>(
  injectSheet(styles, 'ShiftsReport'),
  inject('store'),
  observer
)(ShiftsTotalReport)
